import React from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { getCurrentLanguageSelector } from '@app/reducers/i18n';

import { checkAcl } from '@flow/libs/utils/acl';
import SetContractValueModal from '@app/components/modals/set-contract-value';

import Button from '@app/components/button';

import MoneyIcon from '@app/components/icons/money';
import style from './contract-value.module.scss';

export interface Props {
  agreement?: Oneflow.Agreement;
  agreementValue?: Oneflow.Agreement['agreementValue'];
  className?: string;
  showPlaceholder?: boolean;
  isReadOnly?: boolean;
  showIcon?: boolean;
}

const ContractValueComponent = ({
  agreement,
  agreementValue,
  className,
  showPlaceholder,
  isReadOnly,
  showIcon = true,
}: Props): React.ReactElement | null => {
  const currentLanguage = useSelector(getCurrentLanguageSelector);

  const parseAgreementAmount = (value: NonNullable<Oneflow.Agreement['agreementValue']>): string =>
    value.amount!.toLocaleString(currentLanguage, {
      style: 'currency',
      currency: value.currency!,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
    });

  const renderContractValueText = () => {
    if (agreementValue) {
      return (
        <>
          {showIcon && <MoneyIcon width="14px" />}
          {parseAgreementAmount(agreementValue)}
        </>
      );
    }

    if (showPlaceholder) {
      return '-';
    }

    return null;
  };

  const getChildren = (onClick: () => void) => (
    <Button
      customClass={style.AddButton}
      kind="linkSeparate"
      onClick={onClick}
      disabled={!checkAcl(get(agreement, 'acl'), 'agreement:agreement_value:update:amount')}
    >
      {renderContractValueText()}
    </Button>
  );

  if (!agreement || isReadOnly) {
    return <div className={className}>{renderContractValueText()}</div>;
  }

  if (!agreementValue && !showPlaceholder) {
    return null;
  }

  return (
    <div className={className}>
      <SetContractValueModal agreement={agreement}>{getChildren}</SetContractValueModal>
    </div>
  );
};

export default ContractValueComponent;
